import { SelectionType } from "@swimlane/ngx-datatable";
import { ChartTimeUM } from "src/components/shared-components/enum/enumChart";
import { FiltersType } from "src/components/shared-components/enum/enumFilters";
import { BIGLogicTypeAlarm } from "src/components/shared-components/enum/enumLogic";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { LoggerService } from "src/service/logger.service";
import {
  DataRetrievalErrorCase,
  DateTimeStringFomat,
} from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import {
  ColumnTableType,
  HideShowColumn,
  SortOrder,
} from "src/components/shared-components/enum/enumTableHistory";

import { DateHelper } from "./date-helper";
import { DateTimeFormats } from "./date-time-formats";
import { DateTimeRegex } from "./date-time-regex";

import {
  Column,
  IconInfo,
  SortInfo,
  Subtitle,
  TableInfo,
  TextInfo,
  ValueToChange,
} from "src/components/shared-components/class/general-classes/item-history";
import { EquoboxMedium } from "src/components/shared-components/enum/enumEquobox";
import { ReadingInterval } from "src/components/shared-components/enum/enumReadingInterval";
import { ConsumptionKind } from "src/components/shared-components/enum/enumConsumptionKind";
import { BillQuoteType } from "src/components/shared-components/enum/enumBillQuoteType";
import { Command } from "./command";
import { Filter, FilterValues } from "./filter";
import { AbstractControl, ValidatorFn } from "@angular/forms";

export class StaticClass {
  //#region Date and Time Manipulation
  public static getTimeUMString(value: ChartTimeUM) {
    let returnValue = "Unknown";
    switch (value) {
      case ChartTimeUM.HOUR:
        returnValue = "Ora";
        break;
      case ChartTimeUM.DAY:
        returnValue = "Giorno";
        break;
      case ChartTimeUM.WEEK:
        returnValue = "Settimana";
        break;
      case ChartTimeUM.MONTH:
        returnValue = "Mese";
        break;
      case ChartTimeUM.YEAR:
        returnValue = "Anno";
        break;
      default:
        break;
    }

    return returnValue;
  }

  /**
   * Get chart time um adjective
   *
   * @param value Value to convert
   * @returns
   */
  public static getTimeUMStringAdjective(value: ChartTimeUM) {
    let returnValue = "Unknown";
    switch (value) {
      case ChartTimeUM.HOUR:
        returnValue = "Orario";
        break;
      case ChartTimeUM.DAY:
        returnValue = "Giornaliero";
        break;
      case ChartTimeUM.WEEK:
        returnValue = "Settimanale";
        break;
      case ChartTimeUM.MONTH:
        returnValue = "Mensile";
        break;
      case ChartTimeUM.YEAR:
        returnValue = "Annuale";
        break;
      default:
        break;
    }

    return returnValue;
  }

  /**
   * Reads a string `dd-MM-yyyy HH:mm:ss`, or `dd-MM-yyyy` alone (midnight).
   * @param value string to read
   * @returns the date, invalid when the string is not in that format
   */
  public static getDateFromDateTimeString(value: string): Date {
    return DateHelper.parse(value, DateTimeFormats.DATETIME_FORMAT_SHORT);
  }

  /**
   * This method convert dates follwing the pattern " YYYY-MM-DDTHH:mmZ " (or ISO 8601 ) to pattern "DD-MM-YYYY"
   *
   * @param oldFormatDate
   * @returns
   */
  public static formatDate(oldFormatDate: string): string {
    return DateHelper.format(
      oldFormatDate,
      DateTimeFormats.DATETIME_FORMAT_SHORT_DATE_STRAIGHT
    );
  }

  /**
   * This method convert dates  follwing the pattern " DD-MM-YYYY " and times following the pattern " HH:mm" (or ISO 8601 ) to a single string with pattern " YYYY-MM-DD HH:mm:SS "
   *
   * @param oldFormatDate
   * @returns
   */
  public static unifyDateTime(
    oldFormatDate: string,
    oldFormTime: string
  ): string {
    return DateHelper.format(
      DateHelper.parse(oldFormatDate + oldFormTime, DateTimeFormats.DATETIME_FORMAT_SHORT_SLASH),
      DateTimeFormats.DATETIME_FORMAT
    );
  }

  /**
   * This method convert dates follwing the pattern " DD-MM-YYYY "  to pattern "YYYY-MM-DD"
   *
   * @param oldFormatDate
   * @returns
   */
  public static formatDateReverse(oldFormatDate: string): string {
    return DateHelper.format(
      StaticClass.getDateFromDateTimeString(oldFormatDate),
      DateTimeFormats.DATETIME_FORMAT_SHORT_DATE_INVERSE
    );
  }

  /**This method check if given string:
   *
   * *Contain a date OR a "time" value.
   * *Follows specified pattern.
   *
   * @param dataString the string to check.
   * @param dateStringFormat Enum value, specifying the date/time-format to be expected.
   *  */
  public static checkDateTimeStringFormat(
    dataString: string,
    dateStringFormat: DateTimeStringFomat
  ): boolean {
    let returnValue: boolean = false;

    if (dataString) {
      switch (dateStringFormat) {
        /* Format "DD-MM-YYYY" */
        case DateTimeStringFomat.SHORT_DATE_STRAIGHT:
          if (
            new RegExp(DateTimeRegex.DATE_ddmmyyyy_ONLY_SCORE_REGEXEXPR).test(
              dataString
            )
          ) {
            const dateComponent = dataString.split("-");

            returnValue = this.checkDateDayMounthNumber(dateComponent);
          }
          break;

        /* Format "DD/MM/YYYY" */
        case DateTimeStringFomat.SHORT_DATE_STRAIGHT_SLASH:
          if (
            new RegExp(DateTimeRegex.DATE_ddmmyyyy_ONLY_SLASH_REGEXEXPR).test(
              dataString
            )
          ) {
            const dateComponent = dataString.split("/");

            returnValue = this.checkDateDayMounthNumber(dateComponent);
          }
          break;

        /* Format "YYYY-MM-DD" */
        case DateTimeStringFomat.SHORT_DATE_INVERSE:
          if (
            new RegExp(DateTimeRegex.DATE_yyyymmdd_ONLY_SCORE_REGEXEXPR).test(
              dataString
            )
          ) {
            let dateComponentsplit = dataString.split("-");
            let dateComponent = new Array<string>();

            dateComponent.push(dateComponentsplit[2]);
            dateComponent.push(dateComponentsplit[1]);
            dateComponent.push(dateComponentsplit[0]);

            returnValue = this.checkDateDayMounthNumber(dateComponent);
          }
          break;

        /* Format "D-MM-YYYY" */
        case DateTimeStringFomat.SHORT_DATE_STRAIGHT_SIGLE_DAY:
          if (
            new RegExp(
              DateTimeRegex.DATE_dmmyyyy_ONLY_SCORE_SINGLE_DAY_REGEXEXPR
            ).test(dataString)
          ) {
            const dateComponent = dataString.split("-");

            returnValue = this.checkDateDayMounthNumber(dateComponent);
          }
          break;

        /* Format "DD-M-YYYY" */
        case DateTimeStringFomat.SHORT_DATE_STRAIGHT_SIGLE_MONTH:
          if (
            new RegExp(
              DateTimeRegex.DATE_ddmyyyy_ONLY_SCORE_SINGLE_MONTH_REGEXEXPR
            ).test(dataString)
          ) {
            const dateComponent = dataString.split("-");

            returnValue = this.checkDateDayMounthNumber(dateComponent);
          }
          break;

        /* Format "D-M-YYYY" */
        case DateTimeStringFomat.SHORT_DATE_STRAIGHT_SIGLE_DAY_MONTH:
          if (
            new RegExp(
              DateTimeRegex.DATE_dmyyyy_ONLY_SCORE_SINGLE_DAY_MONTH_REGEXEXPR
            ).test(dataString)
          ) {
            const dateComponent = dataString.split("-");

            returnValue = this.checkDateDayMounthNumber(dateComponent);
          }
          break;

        case DateTimeStringFomat.SHORT_DATE_STRAIGHT_NO_SEPARATORS:
          if (
            new RegExp(
              DateTimeRegex.DATE_ddmmyyyy_NO_SEPARATION_REGEXEXPR
            ).test(dataString)
          ) {
            let dateComponent = new Array<string>();
            dateComponent.push(dataString.substring(0, 2));
            dateComponent.push(dataString.substring(2, 4));
            dateComponent.push(dataString.substring(4, 8));

            returnValue = this.checkDateDayMounthNumber(dateComponent);
          }

          break;

        /* Format "HH:mm"  */
        case DateTimeStringFomat.LONG_HOUR_LONG_MINUTES:
          if (
            new RegExp(DateTimeRegex.TIME_HHMM_COLON_REGEXEXPR).test(dataString)
          ) {
            const timeComponent = dataString.split(":");

            returnValue = this.checkTimeLongHour(timeComponent);
          }

          break;

        /* Format "H:mm"  (24-hour based, BUT without initial ZERO for hour)*/
        case DateTimeStringFomat.SHORT_HOUR_LONG_MINUTES:
          if (
            new RegExp(DateTimeRegex.TIME_HMM_COLON_REGEXEXPR).test(dataString)
          ) {
            const timeComponent = dataString.split(":");

            returnValue = this.checkTimeLongHour(timeComponent);
          }
          break;

        /* Format "HH:m"  (24-hour based, BUT without initial ZERO for minutes)*/
        case DateTimeStringFomat.LONG_HOUR_SHORT_MINUTES:
          if (
            new RegExp(DateTimeRegex.TIME_HHM_COLON_REGEXEXPR).test(dataString)
          ) {
            const timeComponent = dataString.split(":");

            returnValue = this.checkTimeLongHour(timeComponent);
          }
          break;

        /* Format "H:m"  (24-hour based, BUT without initial ZERO for BOTH hours AND minutes))*/
        case DateTimeStringFomat.SHORT_HOUR_SHORT_MINUTES:
          if (
            new RegExp(DateTimeRegex.TIME_HM_COLON_REGEXEXPR).test(dataString)
          ) {
            const timeComponent = dataString.split(":");

            returnValue = this.checkTimeLongHour(timeComponent);
          }
          break;

        /* Format "HHmm"  (24-hour based, BUT without initial ZERO for BOTH hours AND minutes))*/
        case DateTimeStringFomat.LONG_HOUR_LONG_MINUTES_NO_SEPARATION:
          if (
            new RegExp(DateTimeRegex.TIME_HHMM_NO_SEPARATION_REGEXEXPR).test(
              dataString
            )
          ) {
            let timeComponent = new Array<string>();
            timeComponent.push(dataString.substring(0, 2));
            timeComponent.push(dataString.substring(2, 4));

            returnValue = this.checkTimeLongHour(timeComponent);
          }
          break;

        default:
          returnValue = false;

          break;
      }
    }

    return returnValue;
  }

  protected static checkTimeLongHour(timeComponent: string[]): boolean {
    let returnValue = false;
    if (this.checkTimeMinutesString(timeComponent[1]) >= 0) {
      returnValue = this.checkTimeLongHourString(timeComponent[0]) >= 0;
    }

    return returnValue;
  }

  protected static checkTimeMinutesString(minutesStr: string): number {
    let returnValue: number = -1;

    const decimalM = parseInt(minutesStr, 10);
    if (0 <= decimalM && decimalM <= 60) {
      returnValue = decimalM;
    }

    return returnValue;
  }

  protected static checkTimeLongHourString(hourStr: string): number {
    let returnValue: number = -1;

    const decimalH = parseInt(hourStr, 10);

    if (0 <= decimalH && decimalH <= 24) {
      returnValue = decimalH;
    }

    return returnValue;
  }

  /**
   * This method check if a peculiar pair of string could be interpreted as, respectively, a day and the mounth in which this day is contained.
   * @param dateComponent Array of strings. The FIRST element contains a number defining the DAY, the SECOND one contains a number defining the MONTH.
   */
  protected static checkDateDayMounthNumber(dateComponent: string[]): boolean {
    let returnValue = false;
    /* Check  month*/
    const monthInt = this.checkDateMonth(dateComponent[1]);
    if (monthInt > 0) {
      /* Check  day*/
      if (this.checkDateDay(dateComponent[0], monthInt)) {
        returnValue = true;
      }
    }

    return returnValue;
  }

  /**
   * This methof check if a string contains a viable day (expressed as number) for a specific month (expressed as number).
   * @param dayStr  string. The day (expressed as number) to check validity of.
   * @param monthInt Number. The month we are consideraing. This is expressed as number comprised between  0 and 12.
   * @returns
   */
  protected static checkDateDay(dayStr: string, monthInt: number): boolean {
    let returnValue: boolean = false;
    const decimalD = parseInt(dayStr, 10);

    if (decimalD > 0) {
      switch (monthInt) {
        /* April, June, September, November */
        case 4:
        case 6:
        case 9:
        case 11:
          returnValue = decimalD <= 30;
          break;

        /*February (leap) */
        case 2:
          returnValue = decimalD <= 29;
          break;

        /* Jenuary, March, May, July, August, October, December */
        case 1:
        case 3:
        case 5:
        case 7:
        case 8:
        case 10:
        case 12:
          returnValue = decimalD <= 31;
          break;
        default:
          returnValue = false;
          break;
      }
    }

    return returnValue;
  }

  protected static checkDateMonth(monthStr: string): number {
    let returnValue: number = -1;

    const decimalM = parseInt(monthStr, 10);

    if (0 < decimalM && decimalM <= 12) {
      returnValue = decimalM;
    }

    return returnValue;
  }

  //#endregion

  //#region
  /**
   * Convert RGBA to HEX (value of A is not considered)
   * @param rgba RGBA color
   * @returns HEX color
   */
  public static RGBAToHex(rgba: string): string {
    rgba = rgba.substring(rgba.indexOf("(") + 1, rgba.indexOf(")"));
    let rgbaSplit = rgba.split(",");
    let r = Number(rgbaSplit[0]).toString(16);
    let g = Number(rgbaSplit[1]).toString(16);
    let b = Number(rgbaSplit[2]).toString(16);

    if (r.length == 1) r = "0" + r;
    if (g.length == 1) g = "0" + g;
    if (b.length == 1) b = "0" + b;

    return "#" + r + g + b;
  }

  public static hexToRGB(h): string {
    let r = "";
    let g = "";
    let b = "";

    // 3 digits
    if (h.length == 4) {
      r = "0x" + h[1] + h[1];
      g = "0x" + h[2] + h[2];
      b = "0x" + h[3] + h[3];

      // 6 digits
    } else if (h.length == 7) {
      r = "0x" + h[1] + h[2];
      g = "0x" + h[3] + h[4];
      b = "0x" + h[5] + h[6];
    }

    return "rgb(" + +r + "," + +g + "," + +b + ")";
  }
  //#endregion

  public static extractString(str1: string, str2: string): string {
    let resultStr: string = "";

    return resultStr;
  }

  public static GetStateNameByValue(
    States: string,
    Value: string,
    AddMeasureUnit: boolean = false,
    MeasureUnit: string = ""
  ): string {
    let returnValue: string = Value;
    if (AddMeasureUnit) returnValue += " " + MeasureUnit;

    if (States.includes("=" + Value)) {
      const statesSplit: string[] = States.split("|");
      for (let i = 0; i < statesSplit.length; i++) {
        if (statesSplit[i].includes("=" + Value)) {
          const stateValue = statesSplit[i].split("=");
          if (stateValue[1] == Value) {
            returnValue = stateValue[0];
            break;
          }
        }
      }
    }
    return returnValue;
  }

  public static GetValueByState(States: string, State: string): string {
    let returnValue: string = State;

    if (States.includes(State + "=")) {
      const statesSplit: string[] = States.split("|");
      for (let i = 0; i < statesSplit.length; i++) {
        if (statesSplit[i].includes(State + "=")) {
          const stateValue = statesSplit[i].split("=");
          if (stateValue[0] == State) {
            returnValue = stateValue[1];
            break;
          }
        }
      }
    }
    return returnValue;
  }

  /**
   * Return toolbar color (as Ionic class) by network connection type
   *
   * @param connectionType Connection type to get toolbar class
   * @returns Ionic class that experess network sstate
   */
  public static getComponentColorByNetworkState(
    connectionType: ConnectionType
  ): string {
    let toolbarColor: string = "";

    switch (Number(connectionType)) {
      case ConnectionType.NONE:
        toolbarColor = "dark";
        break;

      case ConnectionType.WAN:
      case ConnectionType.LAN:
      case ConnectionType.LANWAN:
        toolbarColor = "primary";
        break;
      default:
        break;
    }

    return toolbarColor;
  }

  //#region SortFunctions
  public static SortAddressFunction(Address1: string, Address2: string) {
    let result = 0;
    if (Address1.includes("/") || Address2.includes("/")) {
      if (!Address1.includes("/")) return -1;
      if (!Address2.includes("/")) return 1;
      const address1Split = Address1.split("/");
      const address2Split = Address2.split("/");

      for (let index = 0; index < address1Split.length; index++) {
        if (address2Split.length >= index) {
          result = this.SortAddressFunction(
            address1Split[index],
            address2Split[index]
          );
          if (result != 0) return result;
        }
      }

      return result;
    } else {
      if (Address1 == Address2) return 0;
      if (!isNaN(parseInt(Address1)) && !isNaN(parseInt(Address2)))
        return parseInt(Address1) < parseInt(Address2) ? 1 : -1;
      else return Address1 < Address2 ? 1 : -1;
    }
  }

  public static SortDateFunction(date1: string, date2: string) {
    const pattern = date1.includes("-")
      ? DateTimeFormats.DATETIME_FORMAT_SHORT
      : DateTimeFormats.DATETIME_API_IT_CULTURE_INFO;
    return DateHelper.parse(date1, pattern).getTime() > DateHelper.parse(date2, pattern).getTime() ? 1 : -1;
  }
  //#endregion

  //#region Interpreter of data from API

  /**
   * Taken a pseudo-boolean value, it returns the proper boolean value associated to it.
   *
   * NOTE:
   * With "pseudo-boolean", we mean a boolean value OR a string value, which must equal to "true"/"TRUE"/"True"/"false"/"FALSE"/"False"/"1"/"0".
   * @param data  A pseudo-boolean value
   * @returns The associated boolean value; if the passed data are null or undefined, it also returns "FALSE".
   */
  public static booleanize(data: any): boolean {
    let res = false;

    if (data !== null && data !== undefined) {
      let boolStr = data?.toString();

      const boolNum = Number(boolStr);
      if (!isNaN(boolNum)) {
        res = boolNum === 0 ? false : true;
      } else {
        res = boolStr?.toUpperCase() === "TRUE";
      }
    }

    //return data?.toString().toUpperCase() === "TRUE";
    return res;
  }

  //#endregion

  //#region Random
  public static MakeRandom(lengthOfCode: number) {
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890";
    let text = "";
    for (let i = 0; i < lengthOfCode; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }
  //#endregion

  //#region Type Alarm
  /**
   * Return the selected alarm icon based on the alarm type id.
   */
  public static GetTypeAlarm(idType: number): string {
    switch (idType) {
      case BIGLogicTypeAlarm.FireAlarm:
        return "flame";
      case BIGLogicTypeAlarm.SupportRequestAlarm:
        return "medkit";
      case BIGLogicTypeAlarm.AntifreezeAlarm:
        return "snow";
      case BIGLogicTypeAlarm.RequestForRoomService:
        return "bed";
      case BIGLogicTypeAlarm.RiskOfFlooding:
        return "water";
      case BIGLogicTypeAlarm.TooHotAlarm:
        return "bonfire";
      case BIGLogicTypeAlarm.TechnicalAlarm:
        return "laptop";
      default:
        return "warning";
    }
  }
  //#endregion

  //#region generic-table
  /**
   * Returns a Column object (object that can be used to create columns for ngx-datatable).
   * @param prop column identifier
   * @param name translation name (eg "dictionary.name" -> name = "name")
   * @param css css class to associate with the column
   * @param type typology of the column
   * @param visible ability to hide the column if the screen size falls below a certain threshold
   */
  public static createColumnObj(
    prop: string,
    name: string,
    css: string,
    type: ColumnTableType,
    visible: HideShowColumn
  ): Column {
    let column: Column = new Column();
    column.Prop = prop;
    column.Name = "dictionary." + name;
    column.Class = css;
    column.Type = type;
    column.Visible = visible;
    return column;
  }

  /**
   * Creates and returns a TextInfo object.
   * @param text text string
   * @param css css class to associate with the text
   */
  public static createTextInfo(text: string, css: string): TextInfo {
    let textInfo: TextInfo = new TextInfo();
    textInfo.Text = text;
    textInfo.CssClass = css;
    return textInfo;
  }

  /**
   * Creates and returns an IconInfo object.
   * @param name icon name
   * @param css css class to associate with the icon
   */
  public static createIconInfo(name: string, css: string): IconInfo {
    let iconInfo: IconInfo = new IconInfo();
    iconInfo.Name = name;
    iconInfo.CssClass = css;
    return iconInfo;
  }

  /**
   * Creates and returns a Subtitle object.
   * @param text
   * @param iconName icon name
   * @param css css class to associate with the icon
   * @returns
   */
  public static createSubtitle(
    text: string,
    textCss: string,
    iconName: string,
    iconCss: string
  ): Subtitle {
    let subtitle: Subtitle = new Subtitle();
    subtitle.TextInfo = this.createTextInfo(text, textCss);
    subtitle.IconInfo = this.createIconInfo(iconName, iconCss);
    return subtitle;
  }

  /**
   * Creates and returns a ValueToChange object.
   * @param id object identifier
   * @param idObj id of the object to which it refers
   * @param value new value
   */
  public static createValueToChange(
    id: string,
    idObj: number,
    value: any
  ): ValueToChange {
    let valueToChange: ValueToChange = new ValueToChange();
    valueToChange.Id = id;
    valueToChange.IdObj = idObj;
    valueToChange.Value = value;
    return valueToChange;
  }

  /**
   * Creates and returns the array to pass to ngx-datatable.
   * @param array array to create the new array with
   * @param valuesToChange array of values to change
   */
  public static createArrayForTable(
    array: any[],
    valuesToChange: ValueToChange[]
  ): any[] {
    let arrayForTable: any[] = [];
    for (let i = 0; i < array.length; i++) {
      let newItem = {};
      for (const [key, value] of Object.entries(array[i])) {
        let valueToChange: ValueToChange = valuesToChange.find(
          (x) => x.Id === key && x.IdObj === i
        );
        if (valueToChange) {
          newItem[key] = valueToChange.Value;
        } else {
          newItem[key] = value;
        }
      }
      arrayForTable.push(newItem);
    }
    return arrayForTable;
  }

  /**
   * Creates and returns a TableInfo object.
   * @param css class to assign to the table
   * @param scroll true: fixed header and pagination of results
   * @param selectionType type of selection possible on ngx-datatable
   * @param columns column array
   * @param isApp
   * @param reload true: if an error occurs, displays the reload button to reload the data
   */
  public static createTableInfo(
    css: string,
    scroll: boolean,
    selectionType: SelectionType,
    columns: Column[],
    isApp: boolean,
    reload: boolean
  ): TableInfo {
    let tableInfo: TableInfo = new TableInfo();
    tableInfo.Filters = [];
    tableInfo.List = [];
    tableInfo.CssClass = css === "" ? "material" : css;
    tableInfo.SelectionType = selectionType;
    tableInfo.ScrollbarV = scroll;
    tableInfo.Columns = columns;
    tableInfo.IsFunctionalityNotImplemented = false;
    tableInfo.ShowErrorContent = false;
    tableInfo.IsApp = isApp;
    tableInfo.IsListInitialized = false;
    tableInfo.Reload = reload;
    tableInfo.Sorts = null;
    tableInfo.RowHeight = null;

    return tableInfo;
  }
  //#endregion

  //#region Filter
  /**
   * Creates and returns a Filter object.
   * @param kind filter type
   * @param name filter identifier
   * @param title translation name (eg "dictionary.name")
   * @param placeHolder placeholder
   * @param filterValue if the filter is of type Combo it must be an array of FilterValue
   * @param input default value
   */
  public static createFilterObject(
    kind: FiltersType,
    name: string,
    title: string,
    placeHolder: string,
    filterValue: FilterValues[],
    input: string
  ): Filter {
    let filter: Filter = new Filter();
    filter.Kind = kind;
    filter.Name = name;
    filter.PlaceHolder = placeHolder;
    filter.Title = title;
    filter.Values = filterValue;
    filter.InputValue = input;
    return filter;
  }

  /**
   * reates and returns a SortInfo object.
   * @param prop column identifier
   * @param direction
   * @returns
   */
  public static createSortInfoObject(
    prop: string,
    direction: SortOrder
  ): SortInfo {
    let sortInfo: SortInfo = new SortInfo();
    sortInfo.Prop = prop;
    sortInfo.Direction = direction;
    return sortInfo;
  }
  //#endregion

  //#region Commands
  public static CreateCommandObjectsFromString(commands: string): Command[] {
    let result: Command[] = [];

    const commandsSplit = commands.split("|");
    for (let i = 0; i < commandsSplit.length; i++) {
      const singleCommandSplit = commandsSplit[i].split(" ");
      const InstanceGUIDAddress = singleCommandSplit[0].split("_");
      let command: Command = new Command();
      command.InstanceGUID = InstanceGUIDAddress[0];
      command.Address = InstanceGUIDAddress.slice(1).join("_");
      command.Timing = parseInt(singleCommandSplit[-1]);
      command.Value = singleCommandSplit.slice(1).slice(0, -1).join(" ");
    }

    return result;
  }
  //#endregion

  /**
   * Gets the required deviceType based on the value of the  Enum EquoboxMedium
   * @param enumEquoboxMediumValue the value of the Enum EquoboxMedium
   * @returns
   */
  public static getEquoboxMediumKeyLabel(
    enumEquoboxMediumValue: EquoboxMedium
  ): string {
    let equoboxMediumKeyLabel: string = "";
    switch (enumEquoboxMediumValue) {
      case EquoboxMedium.EQUOBOX_MEDIUM_OTHER:
        equoboxMediumKeyLabel = "Equobox Medium Other";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_OIL:
        equoboxMediumKeyLabel = "Equobox Medium Oil";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_ELECTRICITY:
        equoboxMediumKeyLabel = "Equobox Medium Electricity";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_GAS:
        equoboxMediumKeyLabel = "Equobox Medium Gas";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT:
        equoboxMediumKeyLabel = "Equobox Medium Heat";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_STEAM:
        equoboxMediumKeyLabel = "Equobox Medium Steam";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HOT_WATER:
        equoboxMediumKeyLabel = "Equobox Medium Hot Water";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_WATER:
        equoboxMediumKeyLabel = "Equobox Medium Water";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_COST_ALLOCATOR:
        equoboxMediumKeyLabel = "Equobox Medium Heat Cost Allocator";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_COOLING_LOAD_METER_VOLUME_RETURN_TEMP_OUTLET:
        equoboxMediumKeyLabel = "Equobox Medium cooling load meter (outlet)";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_COOLING_LOAD_METER_VOLUME_FLOW_TEMP_INLET:
        equoboxMediumKeyLabel = "Equobox Medium Cooling load meter (inlet )";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_VOLUME_FLOW_TEMP_INLET:
        equoboxMediumKeyLabel =
          "Equobox Medium Heat ( flow temperature: inlet)";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_COOLING_LOAD_METER:
        equoboxMediumKeyLabel = "Equobox Medium Heat / Cooling load meter";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_BUS_SYSTEM:
        equoboxMediumKeyLabel = "Equobox Medium Bus / System";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_COLD_WATER:
        equoboxMediumKeyLabel = "Equobox Medium Cold Water";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_DUAL_WATER:
        equoboxMediumKeyLabel = "Equobox Medium Dual Water";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_PRESSURE:
        equoboxMediumKeyLabel = "Equobox Medium Pressure";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_A_D_CONVERTER:
        equoboxMediumKeyLabel = "Equobox Medium A/D Converter";
        break;
      case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_UNKNOWN_MEDIUM:
        equoboxMediumKeyLabel = "Equobox Medium Unknown Medium";
        break;
    }
    return equoboxMediumKeyLabel;
  }

  /**
   * Creates and returns smartgateway reading interval label
   */

  public static createReadingIntervalLabel(
    readingInterval: ReadingInterval
  ): string {
    let readingIntervalLabel: string = "";
    switch (readingInterval) {
      case ReadingInterval.DAILY:
        readingIntervalLabel = "daily";
        break;
      case ReadingInterval.WEEKLY:
        readingIntervalLabel = "weekly";
        break;
      case ReadingInterval.MONTHLY:
        readingIntervalLabel = "monthly";
        break;
    }
    return readingIntervalLabel;
  }

  /**
   * Transforms the uploaded configuration data in the required format; array containing all the
   * properties and associated equipment detail
   * @param selectedEquipments
   * @returns
   */
  public static transformSelectedEquipmentProperty(selectedEquipments) {
    return selectedEquipments.map((equipmentObj) => {
      const {
        equipmentId,
        equipmentName,
        propertyId,
        propertyName,
        propertyUnit,
      } = equipmentObj;

      return propertyId.map((propId, index) => ({
        equipmentId: equipmentId,
        equipmentName: equipmentName,
        propertyId: propId,
        propertyName: propertyName[index],
        propertyUnit: propertyUnit[index],
      }));
    });
  }

  /**
   * Creates consumption kind label for the enum consumption type
   */
  public static getConsumptionKindLabel(
    consumptionKind: ConsumptionKind
  ): string {
    let consumptionKindLabel: string = "";
    switch (consumptionKind) {
      case ConsumptionKind.ELECTRICITY:
        consumptionKindLabel = "electricity";
        break;
      case ConsumptionKind.GAS:
        consumptionKindLabel = "gas";
        break;
      case ConsumptionKind.HEAT:
        consumptionKindLabel = "heat";
        break;
    }
    return consumptionKindLabel;
  }

  /**
   *  Creates bill quote type label for the enum bill quote type
   * @param billQuoteType  bill quote type
   * @returns
   */
  public static createBillQuoteTypeLabel(billQuoteType: BillQuoteType): string {
    let typeLabel: string = "";
    switch (billQuoteType) {
      case BillQuoteType.BILL:
        typeLabel = "bill";
        break;
      case BillQuoteType.QUOTE:
        typeLabel = "quote";
        break;
    }
    return typeLabel;
  }

  /**
   * Formats a measurement value string into a standardized string representation.
   *
   * If the input value can be parsed as a number, it will be formatted to the specified
   * number of decimal digits, using a comma as the decimal separator. If the value is an
   * integer, it will return the integer part only. If the input cannot be parsed as a
   * number, the original value string will be returned.
   *
   * @param valueStr - The measurement value as a string.
   * @param defaultDecimalDigits - The number of decimal digits to display (default is 2).
   * @returns A formatted string representing the measurement value.
   */
  public static formatDecimalValue(
    valueStr: string,
    defaultDecimalDigits: number,
    replacePointWithComma: boolean = false
  ): string {
    const valueNum = parseFloat(valueStr);
    if (isNaN(valueNum)) {
      return valueStr;
    }

    let fixedValue = valueNum.toFixed(defaultDecimalDigits);
    if (replacePointWithComma) {
      fixedValue = fixedValue.replace(".", ",");
    }
    if (valueNum === Math.floor(valueNum)) {
      return Math.floor(valueNum).toString();
    } else {
      return fixedValue;
    }
  }

  /**
   * Format the date in the required format
   * @param date  date to be formatted
   * @returns
   */
  public static isoDateFormatter(date: string): string {
    const day = DateHelper.parse(date, DateTimeFormats.DATETIME_FORMAT_SHORT_DATE_INVERSE);
    if (!DateHelper.isValid(day)) {
      return "Invalid date";
    }
    // The day of the parameter with the current local time, followed by the "Z" the API has always received
    return (
      DateHelper.format(day, DateTimeFormats.DATETIME_FORMAT_SHORT_DATE_INVERSE) +
      "T" +
      DateHelper.format(new Date(), DateTimeFormats.DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS_MILLISECOND) +
      "Z"
    );
  }

  /**
   *   This method use the HTTP Error code inside the HTTP Reponse to a precedent HTTP Request for data AND the current
   * connection status to the API WebServer to define the peculiar "data retrieval error case" where we currently found ourselves;
   *based on this, this component behaves in a different way.
   * @param currentConnection Current connection type to the API WebServer
   * @param errorCodeFromDataRetrieval The HTTP Error code inside the HTTP Reponse to a precedent, failed HTTP Request
   * @returns Value of enum "DataRetrievalErrorCase", idetifyng the current "data retrieval error case".
   */
  public static checkDataRetrievalErrorCase(
    currentConnection: ConnectionType,
    errorCodeFromDataRetrieval: number
  ): DataRetrievalErrorCase {
    let returnedErrorCase: DataRetrievalErrorCase =
      DataRetrievalErrorCase.ERROR_WEBSERVER_GENERIC_ERROR;

    //NOTE: This special type of "data-retrieval" error is not indipendent form the fact that the application
    //is currently connected to a API server or not, therefore we check it separately.
    if (errorCodeFromDataRetrieval == -1) {
      returnedErrorCase = DataRetrievalErrorCase.ERROR_FEATURE_UNSUPPORTED;

    } else {
      if (currentConnection == ConnectionType.NONE) {
        returnedErrorCase = DataRetrievalErrorCase.ERROR_WEBSERVER_UNREACHABLE;
      } else {
        switch (errorCodeFromDataRetrieval) {
          case 0:
            returnedErrorCase =
              DataRetrievalErrorCase.ERROR_CLIENT_CONNECTION_CANCELLED;
            break;
          case 400:
            returnedErrorCase =
              DataRetrievalErrorCase.ERROR_WEBSERVER_CANCELLED_400;
            break;
          case 401:
            returnedErrorCase =
              DataRetrievalErrorCase.ERROR_WEBSERVER_FAILED_AUTHENTIATION;
            break;

          case 404:
            returnedErrorCase =
              DataRetrievalErrorCase.ERROR_WEBSERVER_DATA_NOT_FOUND;
            break;

          case 503:
            returnedErrorCase =
              DataRetrievalErrorCase.ERROR_WEBSERVER_SERVICE_UNAVAILABLE;
            break;

          default:
            returnedErrorCase =
              DataRetrievalErrorCase.ERROR_WEBSERVER_GENERIC_ERROR;
            break;
        }
      }
    }

    LoggerService.logStatic(
      LogLevel.LOG_ERROR,
      "Static-classes",
      "Static-classes",
      "checkDataRetrievalErrorCase()",
      "errorCodeFromDataRetrieval: " + errorCodeFromDataRetrieval + " | Returned error case: " + DataRetrievalErrorCase[returnedErrorCase]
    );

    return returnedErrorCase;
  }

  /**
   * This method calculates and returns a randomly generated duration for a temporal interval, expressed in MILLISECONDS.
   * @param lowerLimitIntervalMs The lower limit of the range of possibile durations, EXPRESSED IN SECONDS.
   * @param upperLimitIntervalMs The upper limit of the range of possibile durations, EXPRESSED IN SECONDS.
   * @param periodToAvoidMs IF DEFINED, the generated duration shall be different than a multiple of thi parameter (EXPRESSED IN SECONDS)
   * @returns The randomly generated duration for a temporal interval, expressed in MILLISECONDS
   */
  public static setTemporalIntervalRandomMs(
    lowerLimitIntervalS: number,
    upperLimitIntervalS: number,
    periodToAvoidS: number = null
  ): number {
    let randomDelay: number = Math.floor(
      Math.random() * (upperLimitIntervalS - lowerLimitIntervalS) +
      lowerLimitIntervalS
    );
    if (periodToAvoidS != null) {
      if (randomDelay % periodToAvoidS == 0) {
        randomDelay = randomDelay + 2;
      }
    }

    randomDelay = randomDelay * 1000;

    return randomDelay;
  }

  /**
   * Utility method; it allows to insert into code execution a custom delay; it does so creating a 
   * Promise which automatically completes after a specified period.
   * @param ms The desired duration for the dealy, expressed in milliseconds.
   * @returns 
   */
  public static setDelay(ms: number): Promise<any> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * This utility method allows to apply a Validator to a FormControl only if a certain condition is met. 
   * If the "condition" function returns TRUE, the Validator is returned (ti be applied on the FormControl). OTHERWISE, it returns NULL.
   * @param condition The "verification function"; it must contain the specific conditions that should be verified in order to apply the Validator.
   * @param validator The "Validator function"; it must contain the specific validation rules that should be applied to the FormControl.
   * @returns 
   */
  public static applyConditionalValidator(condition: () => boolean, validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!condition()) {
        return null;
      }
      return validator(control);
    };
  }

  /**
   * This utility method allows to check if a certain numeric value is present inside a specific Enum as the second part of
   * a "key-value" pair.
   * @param value The value to be checked.
   * @param enumType The specifi Enum in wich the value should be searched.
   * @returns 
   */
  public static isNumberInEnum(value: number, enumType: any): boolean {
    return Object.values(enumType).includes(value);
  }


  /**
   *  This utility method converts a given enum (both Numeric and string) to an array, where each item contains 
   * two properties ("key" and "value") valorized with, respectively, the "key" and the "value" of an specific item of the enum.
   * @param enumObj The (numeric or string) enum to be converted.
   * @returns 
   */
  public static enumToEntriesArray<T extends Record<string, string | number>>(
    enumObj: T
  ): { name: string; value: T[keyof T] }[] {
    return Object.keys(enumObj)
      // drop the reverse-mapping keys that numeric enums add (e.g. "0", "1", "2"); string enums are unaffected
      .filter(key => isNaN(Number(key)))
      .map(key => ({
        name: key,
        value: enumObj[key as keyof T]
      }));
  }


  /**
   * General utility method: it checks if a certain value (number or string) is present inside a given "T"-type enum 
   * @param enm  The specific enum in which the value should be searched; it is passed as a parameter of type "T".
   * @param value The value to be checked; NOTE that it should be casted as number OR string.
   * @returns True if the value is present in the enum, false otherwise.
   */
  public static enumHasValue<T>(enm: T, value: unknown): boolean {
    const vals = Object.values(enm).filter(v => typeof v !== 'string' || isNaN(Number(v)));
    return vals.includes(value as any);
  }


  /**This utility method convert a generic object to an unsigned byte array.
   * 
   * NOTE that each unsigned byte is codified in 3 digits and can be see as a integer in the range 0-255. 
   */
  public static convertObjectToUnsignedByteArray(value: string | any): number[] {
    let bytes: number[] = [];

    if (typeof value === "string") {
      // comma-separated signed bytes string
      bytes = value
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((s) => {
          const n = parseInt(s, 10);
          return (isNaN(n) ? 0 : (n < 0 ? n + 256 : n)) & 0xff;
        });
    } else if (Array.isArray(value)) {
      // already an array of numbers (possibly signed)
      bytes = (value as any[]).map((n) => {
        const num = Number(n);
        return (isNaN(num) ? 0 : (num < 0 ? num + 256 : num)) & 0xff;
      });
    } else if (value instanceof Uint8Array || (typeof Buffer !== "undefined" && value instanceof Buffer)) {
      bytes = Array.from(value as Uint8Array);
    } else if (value && value.words && typeof value.sigBytes === "number") {
      // CryptoJS WordArray -> convert to bytes
      const wa: any = value;
      bytes = [];
      for (let i = 0; i < wa.sigBytes; i++) {
        const word = wa.words[i >>> 2];
        bytes.push((word >>> (24 - (i % 4) * 8)) & 0xff);
      }
    } else {
      // fallback: try toString then parse, or bail out
      if (value != null && typeof value.toString === "function") {
        const s = value.toString();
        if (s.includes(",")) {
          return this.convertObjectToUnsignedByteArray(s); // recursive parse
        }
      }

      LoggerService.logStatic(
        LogLevel.LOG_ERROR,
        "Static-classes",
        "Static-classes",
        "convertObjectToUnsignedByteArray()",
        "Unsupported input type for value. No fallback possible."
      );
      bytes = [];
    }
    return bytes;
  }

  /**
   * *This utility method convert a generic object to a signed byte array.
   *
   * NOTE that each signed byte is codified in 2 digits and can be see as a integer in the range [-128 , 127].
   * @param value The input value to convert.
   * @returns An array of signed 8-bit bytes.
   */
  public static convertObjectToSignedByteArray(value: string | any): number[] {
    // NOTE: this implementation returns SIGNED 8-bit bytes in range -128..127
    // (keeps the original method name for compatibility)
    const toSigned8 = (n: number): number => {
      const b = n & 0xff;
      return b > 127 ? b - 256 : b;
    };

    const tryParseNumberToken = (tok: string): number => {
      if (!tok) return 0;
      // allow decimal or hex tokens (0x..)
      if (/^0x[0-9a-fA-F]+$/.test(tok)) {
        return toSigned8(parseInt(tok, 16));
      }
      const n = Number(tok);
      return Number.isNaN(n) ? 0 : toSigned8(n);
    };

    let bytes: number[] = [];

    // string input
    if (typeof value === "string") {
      const s = value.trim();

      // JSON array string like "[-38,122,...]"
      if (s.startsWith("[") && s.endsWith("]")) {
        try {
          const arr = JSON.parse(s);
          if (Array.isArray(arr)) {
            return arr.map((n: any) => {
              const num = Number(n);
              return Number.isNaN(num) ? 0 : toSigned8(num);
            });
          }
        } catch (e) {
          // fallthrough to other parsing
        }
      }

      // comma/space separated signed decimals, e.g. "-38, 122, -115" or "-38 122 -115"
      if (s.includes(",") || /\s+/.test(s)) {
        const tokens = s.split(/[\s,]+/).map(t => t.trim()).filter(t => t.length > 0);
        bytes = tokens.map(tryParseNumberToken);
        return bytes;
      }

      // plain hex string (even length) e.g. "F653FA...". 
      //ATTENTION: In this case, the string MUST have a even length, otherwise we cannot be sure about the correct parsing of the last byte (e.g. "F65" -> is it [0x0F, 0x65] or [0xF6, 0x50] ?)
      if (/^[0-9A-Fa-f]+$/.test(s) && (s.length % 2 === 0)) {
        const hex = s;
        for (let i = 0; i < hex.length; i += 2) {
          const byte = parseInt(hex.substr(i, 2), 16);
          bytes.push(toSigned8(byte));
        }
        return bytes;
      }

      // single number string
      const single = Number(s);
      if (!Number.isNaN(single)) {
        return [toSigned8(single)];
      }

      // fallback: try toString parse with commas
      const maybe = s.toString();
      if (maybe.includes(",")) {
        const tokens = maybe.split(",").map(t => t.trim()).filter(t => t.length > 0);
        return tokens.map(tryParseNumberToken);
      }

      LoggerService.logStatic(
        LogLevel.LOG_WARNING,
        "Static-classes",
        "Static-classes",
        "convertObjectToSignedByteArray()",
        "Unsupported string format. Returning []"
      );
      return [];
    }

    // Array input (of numbers or numeric strings)
    if (Array.isArray(value)) {
      return (value as any[]).map((n) => {
        const num = Number(n);
        return Number.isNaN(num) ? 0 : toSigned8(num);
      });
    }

    // Uint8Array or Buffer
    if (value instanceof Uint8Array || (typeof Buffer !== "undefined" && value instanceof Buffer)) {
      return Array.from(value as Uint8Array).map(b => toSigned8(b));
    }

    // CryptoJS WordArray
    if (value && value.words && typeof value.sigBytes === "number") {
      const wa: any = value;
      const out: number[] = [];
      for (let i = 0; i < wa.sigBytes; i++) {
        const word = wa.words[i >>> 2];
        const byte = (word >>> (24 - (i % 4) * 8)) & 0xff;
        out.push(toSigned8(byte));
      }
      return out;
    }

    // fallback: try to stringify and parse as comma-separated
    if (value != null && typeof value.toString === "function") {
      const s = value.toString();
      if (s.includes(",")) {
        return this.convertObjectToSignedByteArray(s);
      }
    }

    LoggerService.logStatic(
      LogLevel.LOG_ERROR,
      "Static-classes",
      "Static-classes",
      "convertObjectToSignedByteArray()",
      "Unsupported input type for value. No fallback possible."
    );

    return [];
  }


  /**
 * This utility method returns a 32-char copy of the input string, padded with '0' on the right if shorter,
 * or truncated to 32 chars if longer.
 */
  public static padTo32Zeros(input: string | null | undefined): string {
    const s = (input ?? "").toString();
    return s.length >= 32 ? s.slice(0, 32) : s.padEnd(32, "0");
  }

  /**
   * This method checks if the given value is a string which is not null, undefined  or empty.
   * @param value The value to check.
   * @returns True if the value is a non-empty string, false otherwise.
   */
  public static isNonEmptyString(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0;
  }


  /** 
   * Check if the value holds another address rather than a value, in the
   * INSTANCEGUID_ADDRESS form ("value from address" of the scenario commands)
   * @param value value to check
   */
  public static isAddressReference(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_.+/i.test(
      String(value ?? "").trim()
    );
  }

  /**
   * Get the value to show: for another address only the address, without the instanceGUID
   * (the GUID has no "_", the address may), the value itself otherwise
   * @param value value to show
   */
  public static removeInstanceGUID(value: string): string {
    const text: string = String(value ?? "");

    return StaticClass.isAddressReference(text)
      ? text.substring(text.indexOf("_") + 1)
      : text;
  }

}
